import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Edit, Trash2, Search } from "lucide-react";
import { format } from "date-fns";

const TimesheetTable = ({ entries, loading, onEdit, onDelete }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const filteredEntries = entries.filter((entry) => {
    const search = searchTerm.toLowerCase();
    return (
      entry.employee_name.toLowerCase().includes(search) ||
      entry.machine_id.toLowerCase().includes(search) ||
      entry.machine_name.toLowerCase().includes(search) ||
      entry.workorder_number.toLowerCase().includes(search) ||
      entry.date.includes(search)
    );
  });

  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return "0h 0m";
    
    // Parse time with seconds support (HH:MM or HH:MM:SS)
    const parseTime = (timeStr) => {
      const parts = timeStr.split(':').map(Number);
      return {
        hours: parts[0] || 0,
        minutes: parts[1] || 0,
        seconds: parts[2] || 0
      };
    };
    
    const start = parseTime(startTime);
    const end = parseTime(endTime);
    
    // Convert to total seconds from midnight
    const startSeconds = start.hours * 3600 + start.minutes * 60 + start.seconds;
    const endSeconds = end.hours * 3600 + end.minutes * 60 + end.seconds;
    
    // Calculate duration (handle midnight crossing)
    let durationSeconds = endSeconds - startSeconds;
    if (durationSeconds < 0) {
      // Session crossed midnight
      durationSeconds += 24 * 3600;
    }
    
    const hours = Math.floor(durationSeconds / 3600);
    const minutes = Math.floor((durationSeconds % 3600) / 60);
    
    return `${hours}h ${minutes}m`;
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
  };

  const handleDelete = () => {
    if (deleteId) {
      onDelete(deleteId);
      setDeleteId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
        <p className="mt-4 text-gray-300">Loading entries...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          data-testid="search-input"
          type="text"
          placeholder="Search entries..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-gray-800 border-gray-600 text-white placeholder:text-gray-400"
        />
      </div>

      {/* Table */}
      {filteredEntries.length === 0 ? (
        <div data-testid="no-entries" className="text-center py-12">
          <p className="text-gray-300 text-lg">No entries found</p>
          <p className="text-gray-400 text-sm mt-2">
            {searchTerm
              ? "Try adjusting your search"
              : "Add your first timesheet entry to get started"}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-700">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-800/50 border-gray-700">
                <TableHead className="font-semibold text-gray-200">Date</TableHead>
                <TableHead className="font-semibold text-gray-200">Employee</TableHead>
                <TableHead className="font-semibold text-gray-200">Work Order</TableHead>
                <TableHead className="font-semibold text-gray-200">Machine ID</TableHead>
                <TableHead className="font-semibold text-gray-200">Machine Name</TableHead>
                <TableHead className="font-semibold text-gray-200">Start Time</TableHead>
                <TableHead className="font-semibold text-gray-200">End Time</TableHead>
                <TableHead className="font-semibold text-gray-200">Duration</TableHead>
                <TableHead className="font-semibold text-gray-200">Notes</TableHead>
                <TableHead className="font-semibold text-gray-200 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEntries.map((entry) => (
                <TableRow key={entry.id} className="hover:bg-gray-800/30 border-gray-700">
                  <TableCell className="font-medium text-white">
                    {format(new Date(entry.date), "MMM dd, yyyy")}
                  </TableCell>
                  <TableCell className="text-gray-100">{entry.employee_name}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30">
                      {entry.workorder_number}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                      {entry.machine_id}
                    </span>
                  </TableCell>
                  <TableCell className="text-gray-100">{entry.machine_name}</TableCell>
                  <TableCell className="text-gray-100">{entry.start_time}</TableCell>
                  <TableCell className="text-gray-100">{entry.end_time}</TableCell>
                  <TableCell>
                    <span className="font-medium text-green-400">
                      {calculateDuration(entry.start_time, entry.end_time)}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-xs truncate text-gray-300">
                    {entry.notes || "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        data-testid={`edit-button-${entry.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(entry)}
                        className="text-gray-400 hover:bg-blue-500/20 hover:text-blue-400"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        data-testid={`delete-button-${entry.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => confirmDelete(entry.id)}
                        className="text-gray-400 hover:bg-red-500/20 hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              timesheet entry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-button"
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TimesheetTable;